import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  List,
  ListItem,
  Text,
  useDisclosure,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { apiRequest } from 'src/utils/fetchUtils';
import { useAuth } from 'src/utils/auth';

interface MarketplaceOption {
  id: number;
  name: string;
  slug: string;
}

interface MarketplaceSelectorProps {
  selectedMarketplaceId?: number;
  onMarketplaceSelect: (marketplace: MarketplaceOption | null) => void;
  placeholder?: string;
  isRequired?: boolean;
}

const MarketplaceSelector: React.FC<MarketplaceSelectorProps> = ({
  selectedMarketplaceId,
  onMarketplaceSelect,
  placeholder = 'Search marketplace...',
  isRequired = false,
}) => {
  const [marketplaces, setMarketplaces] = useState<MarketplaceOption[]>([]);
  const [filteredMarketplaces, setFilteredMarketplaces] = useState<MarketplaceOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [selectedMarketplace, setSelectedMarketplace] = useState<MarketplaceOption | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { getToken } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load marketplaces on component mount
  useEffect(() => {
    const loadMarketplaces = async () => {
      try {
        setIsLoading(true);
        const token = await getToken();
        const response = await apiRequest(
          'GET',
          `${process.env.NEXT_PUBLIC_API_HOST}/marketplaces/admin/all/`,
          token,
        );
        setMarketplaces(response);
        setFilteredMarketplaces(response);
      } catch (error) {
        console.error('Failed to load marketplaces:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadMarketplaces();
  }, [getToken]);

  // Set initial selected marketplace when data loads
  useEffect(() => {
    if (selectedMarketplaceId && marketplaces.length > 0) {
      const marketplace = marketplaces.find((m) => m.id === selectedMarketplaceId);
      if (marketplace) {
        setSelectedMarketplace(marketplace);
        if (!isSearching) {
          setInputValue(`${marketplace.name} (${marketplace.slug})`);
        }
      }
    }
  }, [selectedMarketplaceId, marketplaces, isSearching]);

  // Filter marketplaces based on search term
  useEffect(() => {
    if (!searchTerm || !isSearching) {
      setFilteredMarketplaces(marketplaces);
    } else {
      const filtered = marketplaces.filter(
        (marketplace) =>
          marketplace.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          marketplace.slug.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredMarketplaces(filtered);
    }
  }, [searchTerm, marketplaces, isSearching]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
        // Reset to selected marketplace display if we were searching
        if (selectedMarketplace && isSearching) {
          setInputValue(`${selectedMarketplace.name} (${selectedMarketplace.slug})`);
          setSearchTerm('');
          setIsSearching(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);

      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, [onClose, selectedMarketplace, isSearching]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setSearchTerm(value);
    setIsSearching(true);
    if (!isOpen) {
      onOpen();
    }
  };

  const handleMarketplaceSelect = (marketplace: MarketplaceOption) => {
    setSelectedMarketplace(marketplace);
    setInputValue(`${marketplace.name} (${marketplace.slug})`);
    setSearchTerm('');
    setIsSearching(false);
    onMarketplaceSelect(marketplace);
    onClose();
  };

  const handleInputFocus = () => {
    onOpen();
    if (selectedMarketplace && !isSearching) {
      // Clear the input when user starts searching
      setInputValue('');
      setIsSearching(true);
    }
  };

  const handleInputBlur = () => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
    }

    // Delay to allow click on dropdown items to register
    blurTimeoutRef.current = setTimeout(() => {
      if (!isOpen && selectedMarketplace && isSearching) {
        setInputValue(`${selectedMarketplace.name} (${selectedMarketplace.slug})`);
        setSearchTerm('');
        setIsSearching(false);
      }
    }, 150);
  };

  return (
    <FormControl isRequired={isRequired}>
      <FormLabel>Source Marketplace</FormLabel>
      <Box ref={containerRef} position="relative">
        <InputGroup>
          <Input
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={isLoading ? 'Loading marketplaces...' : placeholder}
            disabled={isLoading}
            autoComplete="off"
          />
          <InputRightElement>
            {isLoading ? (
              <Spinner size="sm" />
            ) : (
              <Icon as={isOpen ? ChevronUpIcon : ChevronDownIcon} />
            )}
          </InputRightElement>
        </InputGroup>

        {isOpen && !isLoading && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            zIndex={10}
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            maxHeight="200px"
            overflowY="auto"
            shadow="lg"
          >
            <List>
              {filteredMarketplaces.length > 0 ? (
                filteredMarketplaces.map((marketplace) => (
                  <ListItem
                    key={marketplace.id}
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: 'gray.100' }}
                    onClick={() => handleMarketplaceSelect(marketplace)}
                  >
                    <Text fontWeight="medium">{marketplace.name}</Text>
                    <Text fontSize="sm" color="gray.600">
                      {marketplace.slug}
                    </Text>
                  </ListItem>
                ))
              ) : (
                <ListItem px={3} py={2}>
                  <Text color="gray.500">No marketplaces found</Text>
                </ListItem>
              )}
            </List>
          </Box>
        )}
      </Box>
    </FormControl>
  );
};

export default MarketplaceSelector;
